/**
 * MommHandlers.js
 * 
 * This file contains handler functions for the toolbar buttons in the MOMM page.
 */

import { getImageDescription } from "../../api-helper";

/**
 * Extracts base64 data from a data URL or returns the base64 string directly
 * @param {string} dataUrl - Data URL or base64 string
 * @returns {string} Base64 data without prefix
 */
export const extractBase64 = (dataUrl) => {
  if (!dataUrl) return null;
  
  try {
    // Check if it's already a base64 string without data URL prefix
    if (!dataUrl.includes('data:') && !dataUrl.includes(';base64,')) {
      return dataUrl;
    }
    
    // Extract base64 data from data URL
    const base64Data = dataUrl.split(';base64,')[1];
    return base64Data;
  } catch (error) {
    console.error("Error extracting base64 data:", error);
    return null;
  }
};

/**
 * Copies an image to the clipboard
 * @param {string} imageUrl - URL or base64 data of the image
 */
export const handleCopyToClipboard = async (imageUrl) => {
  try {
    console.log("Copying image to clipboard:", imageUrl?.substring(0, 50) + "...");
    
    // Extract base64 data
    const base64Data = extractBase64(imageUrl);
    
    if (!base64Data) {
      console.error("Failed to extract base64 data from image URL");
      throw new Error("Failed to extract image data");
    }
    
    // Create a blob from the base64 data
    const blob = await fetch(`data:image/png;base64,${base64Data}`).then(r => r.blob());
    
    // Copy to clipboard
    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob
      })
    ]);
    
    // Show success notification
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
    toast.textContent = 'Image copied to clipboard!';
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 2000);
    
  } catch (error) {
    console.error("Error copying image to clipboard:", error);
    
    // Show error notification
    const errorToast = document.createElement('div');
    errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
    errorToast.textContent = `Error copying image: ${error.message || 'Unknown error'}`;
    document.body.appendChild(errorToast);
    
    setTimeout(() => {
      errorToast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 300);
    }, 2000);
  }
};

/**
 * Detects MIME type from base64 data header
 * @param {string} base64Data - Base64 encoded image data
 * @returns {string} MIME type (defaults to 'image/png')
 */
const detectMimeType = (base64Data) => {
  if (!base64Data || typeof base64Data !== 'string') return 'image/png';

  // Check for common image format signatures in base64
  if (base64Data.startsWith('/9j/') || base64Data.startsWith('iVBOR')) {
    return base64Data.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
  } else if (base64Data.startsWith('R0lGOD')) {
    return 'image/gif';
  } else if (base64Data.startsWith('UklGR')) {
    return 'image/webp';
  } else if (base64Data.startsWith('Qk')) {
    return 'image/bmp';
  }

  // Default to PNG
  return 'image/png';
};

/**
 * Validates base64 image data
 * @param {string} base64Data - Base64 encoded image data
 * @returns {boolean} True if valid base64 image data
 */
const validateBase64ImageData = (base64Data) => {
  if (!base64Data || typeof base64Data !== 'string') return false;

  try {
    // Check if it's valid base64
    atob(base64Data);

    // Check if it has image signature
    const mimeType = detectMimeType(base64Data);
    return mimeType.startsWith('image/');
  } catch (error) {
    return false;
  }
};

/**
 * Gets file extension from MIME type
 * @param {string} mimeType - MIME type
 * @returns {string} File extension
 */
const getFileExtension = (mimeType) => {
  const mimeToExt = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp'
  };
  return mimeToExt[mimeType] || 'png';
};

/**
 * Determines if the input is a URL or base64 data
 * @param {string} input - Input string to check
 * @returns {boolean} True if it's a URL, false if it's base64 data
 */
const isUrl = (input) => {
  if (!input || typeof input !== 'string') return false;
  if (input.startsWith('data:')) return false;
  try {
    const url = new URL(input);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Saves an image to the device
 * @param {string} imageUrl - URL or base64 data of the image
 * @param {string} imageName - Name to use for the saved file
 */
export const handleSaveImage = async (imageUrl, imageName = "momm_image") => {
  try {
    console.log("Saving image to device:", imageUrl?.substring(0, 50) + "...");

    let blob;
    let mimeType = 'image/png';
    let fileExtension = 'png';

    // Check if input is a URL or base64 data
    if (isUrl(imageUrl)) {
      console.log("Processing URL:", imageUrl);

      // Handle URL - fetch the image
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }

        blob = await response.blob();
        mimeType = blob.type || 'image/png';
        fileExtension = getFileExtension(mimeType);

        console.log(`Fetched image from URL - MIME type: ${mimeType}, size: ${blob.size} bytes`);
      } catch (fetchError) {
        console.error("Error fetching image from URL:", fetchError);
        throw new Error(`Failed to download image: ${fetchError.message}`);
      }
    } else {
      console.log("Processing base64 data");

      // Handle base64 data
      const base64Data = extractBase64(imageUrl);

      if (!base64Data) {
        console.error("Failed to extract base64 data from image URL");
        throw new Error("Failed to extract image data");
      }

      // Validate base64 data
      if (!validateBase64ImageData(base64Data)) {
        console.error("Invalid base64 image data");
        throw new Error("Invalid image data format");
      }

      // Detect MIME type and get appropriate file extension
      mimeType = detectMimeType(base64Data);
      fileExtension = getFileExtension(mimeType);

      console.log(`Detected MIME type: ${mimeType}, using extension: ${fileExtension}`);

      // Create blob from base64 data
      try {
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: mimeType });
      } catch (error) {
        console.error("Error creating blob from base64 data:", error);
        throw new Error("Failed to process image data");
      }
    }

    // Generate filename with correct extension
    const fileName = `${imageName || 'momm_image'}_${Date.now()}.${fileExtension}`;

    // Check if running in Electron
    const isElectron = !!(window.process && window.process.type) ||
                       !!(window.navigator && window.navigator.userAgent.toLowerCase().includes('electron'));

    // Try to use modern File System Access API first (if available and NOT in Electron)
    if ('showSaveFilePicker' in window && !isElectron) {
      try {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{
            description: 'Image files',
            accept: {
              [mimeType]: [`.${fileExtension}`]
            }
          }]
        });

        const writableStream = await fileHandle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();

        console.log("Image saved using File System Access API");

        // Show success notification
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
        toast.textContent = 'Image saved successfully!';
        document.body.appendChild(toast);

        setTimeout(() => {
          toast.style.opacity = '0';
          setTimeout(() => {
            if (document.body.contains(toast)) {
              document.body.removeChild(toast);
            }
          }, 300);
        }, 2000);

        return; // Success - exit early
      } catch (fsError) {
        if (fsError.name === 'AbortError') {
          console.log("File System Access API cancelled by user");
        } else {
          console.error("File System Access API error:", fsError);
          const errorToast = document.createElement('div');
          errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
          errorToast.textContent = 'Error saving image. Please try again.';
          document.body.appendChild(errorToast);
          setTimeout(() => {
            errorToast.style.opacity = '0';
            setTimeout(() => {
              if (document.body.contains(errorToast)) {
                document.body.removeChild(errorToast);
              }
            }, 300);
          }, 2000);
        }
        return;
      }
    }

    // Fallback: Create download link (traditional method)
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up object URL
    URL.revokeObjectURL(objectUrl);

    console.log("Image saved using traditional download method");

    // Show success notification
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
    toast.textContent = 'Image download started!';
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 2000);

  } catch (error) {
    console.error("Error saving image to device:", error);

    // Determine specific error message
    let errorMessage = 'Unknown error occurred while saving image';
    if (error.message.includes('extract image data')) {
      errorMessage = 'Could not process image data';
    } else if (error.message.includes('Invalid image data')) {
      errorMessage = 'Image data is corrupted or invalid';
    } else if (error.message.includes('process image data')) {
      errorMessage = 'Failed to convert image for saving';
    } else if (error.name === 'NetworkError') {
      errorMessage = 'Network error - check your connection';
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Show error notification
    const errorToast = document.createElement('div');
    errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
    errorToast.textContent = `Error saving image: ${errorMessage}`;
    document.body.appendChild(errorToast);

    setTimeout(() => {
      errorToast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 300);
    }, 3000); // Show error for longer duration
  }
};

/**
 * Shows a dialog to tag an image
 * @param {string} imageUrl - URL or base64 data of the image
 * @param {string} imageHash - Hash of the image for identification
 */
export const handleTagImage = (imageUrl, imageHash) => {
  try {
    console.log("Tagging image:", imageHash);
    
    // Create a dialog for tagging
    const dialog = document.createElement('div');
    dialog.className = 'fixed inset-0 flex items-center justify-center z-50';
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'absolute inset-0 bg-black/50';
    overlay.onclick = () => {
      document.body.removeChild(dialog);
    };
    
    // Create dialog content
    const content = document.createElement('div');
    content.className = 'bg-indigo-900 rounded-lg shadow-xl p-6 w-96 relative z-10';
    
    // Create title
    const title = document.createElement('h3');
    title.className = 'text-xl font-bold text-white mb-4';
    title.textContent = 'Tag Image';
    
    // Create form
    const form = document.createElement('div');
    form.className = 'space-y-4';
    
    // Create input
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Enter tags (comma separated)';
    input.className = 'w-full px-4 py-2 rounded bg-indigo-800 text-white border border-indigo-700 focus:border-purple-500 focus:outline-none';
    
    // Create buttons
    const buttons = document.createElement('div');
    buttons.className = 'flex justify-end space-x-2 mt-4';
    
    const cancelButton = document.createElement('button');
    cancelButton.className = 'px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded';
    cancelButton.textContent = 'Cancel';
    cancelButton.onclick = () => {
      document.body.removeChild(dialog);
    };
    
    const saveButton = document.createElement('button');
    saveButton.className = 'px-4 py-2 bg-purple-700 hover:bg-purple-600 text-white rounded';
    saveButton.textContent = 'Save Tags';
    saveButton.onclick = () => {
      const tags = input.value;
      console.log("Saving tags for image:", { imageHash, tags });
      
      // Here you would save the tags to your database
      // For now, just show a success message
      
      document.body.removeChild(dialog);
      
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
      toast.textContent = 'Tags saved successfully!';
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.style.opacity = '0';
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 2000);
    };
    
    // Assemble the dialog
    buttons.appendChild(cancelButton);
    buttons.appendChild(saveButton);
    form.appendChild(input);
    form.appendChild(buttons);
    content.appendChild(title);
    content.appendChild(form);
    dialog.appendChild(overlay);
    dialog.appendChild(content);
    
    // Show the dialog
    document.body.appendChild(dialog);
    
    // Focus the input
    setTimeout(() => {
      input.focus();
    }, 100);
    
  } catch (error) {
    console.error("Error in tag image handler:", error);
    
    const errorToast = document.createElement('div');
    errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
    errorToast.textContent = `Error tagging image: ${error.message || 'Unknown error'}`;
    document.body.appendChild(errorToast);
    
    setTimeout(() => {
      errorToast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 300);
    }, 2000);
  }
};

/**
 * Shows a dialog with an AI-generated description of an image
 * @param {string} imageUrl - URL or base64 data of the image
 * @param {string} imageHash - Hash of the image for identification
 */
export const handleDescribeImage = async (imageUrl, imageHash) => {
  try {
    console.log("Describing image:", imageHash);
    
    // Create loading notification
    const loadingToast = document.createElement('div');
    loadingToast.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300 flex items-center gap-2';
    
    const spinner = document.createElement('div');
    spinner.className = 'animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full';
    
    loadingToast.appendChild(spinner);
    loadingToast.appendChild(document.createTextNode('Analyzing image...'));
    document.body.appendChild(loadingToast);
    
    // Extract base64 data
    const base64Data = extractBase64(imageUrl);
    
    if (!base64Data) {
      throw new Error("Failed to extract image data");
    }
    
    // Get account number from localStorage or use different methods
    let accountNo = "";
    
    // Try different methods to get the account number
    if (localStorage.getItem('accountNo')) {
      accountNo = localStorage.getItem('accountNo');
    } else if (localStorage.getItem('account')) {
      try {
        const accountData = JSON.parse(localStorage.getItem('account'));
        if (accountData && accountData.accountNo) {
          accountNo = accountData.accountNo;
        }
      } catch (e) {
        console.warn("Could not parse account data from localStorage");
      }
    }
    
    console.log("Using account number:", accountNo || "None (anonymous)");
    
    // Call the real API to get image description
    let imageDescription = "";
    let apiSucceeded = false;
    
    try {
      // First try with hash if available
      if (imageHash) {
        console.log("Attempting to get description using hash:", imageHash);
        const response = await getImageDescription(accountNo, imageHash, "");
        
        if (response && response.image_description) {
          imageDescription = response.image_description;
          apiSucceeded = true;
          console.log("Successfully got description using hash");
        }
      }
      
      // If hash didn't work or isn't available, try with image data
      if (!apiSucceeded) {
        console.log("Attempting to get description using image data");
        const response = await getImageDescription(accountNo, "", base64Data);
        
        if (response && response.image_description) {
          imageDescription = response.image_description;
          apiSucceeded = true;
          console.log("Successfully got description using image data");
        }
      }
      
      // If API failed to return a description
      if (!apiSucceeded) {
        console.warn("API returned but with no description");
        imageDescription = "This appears to be an AI-generated image. The system couldn't provide a more detailed description at this time.";
      }
    } catch (apiError) {
      console.error("API error when describing image:", apiError);
      
      // Provide a fallback description based on available data
      if (apiError.response && apiError.response.status === 401) {
        imageDescription = "Login required: Please log in to use the image description feature.";
      } else if (apiError.message && apiError.message.includes("Network Error")) {
        imageDescription = "Network error: Could not connect to the description service. Please check your internet connection.";
      } else {
        imageDescription = "This appears to be an AI-generated image. The description service is currently unavailable.";
      }
    }
    
    // Remove loading toast
    if (document.body.contains(loadingToast)) {
      document.body.removeChild(loadingToast);
    }
    
    // Create a dialog to display the description
    const dialog = document.createElement('div');
    dialog.className = 'fixed inset-0 flex items-center justify-center z-50';
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'absolute inset-0 bg-black/50';
    overlay.onclick = () => {
      document.body.removeChild(dialog);
    };
    
    // Create dialog content
    const content = document.createElement('div');
    content.className = 'bg-indigo-900 rounded-lg shadow-xl p-6 w-96 relative z-10 max-w-lg';
    
    // Create title
    const title = document.createElement('h3');
    title.className = 'text-xl font-bold text-white mb-4';
    title.textContent = 'Image Description';
    
    // Create description text with real API response
    const description = document.createElement('p');
    description.className = 'text-white mb-4';
    description.textContent = imageDescription;
    
    // Create close button
    const closeButton = document.createElement('button');
    closeButton.className = 'w-full px-4 py-2 bg-purple-700 hover:bg-purple-600 text-white rounded';
    closeButton.textContent = 'Close';
    closeButton.onclick = () => {
      document.body.removeChild(dialog);
    };
    
    // Assemble the dialog
    content.appendChild(title);
    content.appendChild(description);
    content.appendChild(closeButton);
    dialog.appendChild(overlay);
    dialog.appendChild(content);
    
    // Show the dialog
    document.body.appendChild(dialog);
    
  } catch (error) {
    console.error("Error describing image:", error);
    
    // Remove loading toast if it exists
    const loadingToast = document.querySelector('.fixed.top-4.right-4.bg-blue-600');
    if (loadingToast && document.body.contains(loadingToast)) {
      document.body.removeChild(loadingToast);
    }
    
    // Show error notification
    const errorToast = document.createElement('div');
    errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
    errorToast.textContent = `Error describing image: ${error.message || 'Unknown error'}`;
    document.body.appendChild(errorToast);
    
    setTimeout(() => {
      errorToast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 300);
    }, 2000);
  }
}; 